import React, { useState } from 'react'
import Header from '../components/Header'
import { Footer } from '../components/SharedLayout'
import GiftCard from '../components/ui/gift-card'
import { Star, Wrench, Check, ArrowRight, Calendar, Clock, Shield, Zap, MapPin, Mail, Users, Award, Timer } from 'lucide-react'

const OctoberOfferPage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<'basic' | 'full'>('basic')
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  })
  const accentColor = '#AB9381'
  const bgCream = 'rgb(246, 241, 237)'
  const heading = 'rgb(15,15,15)'
  const sub = 'rgb(76,76,75)'
  const pillBg = 'rgb(237, 227, 217)'

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Handle form submission here
    console.log('Form submitted:', formData)
    setShowLeadForm(false)
    // You can add actual form submission logic here
  }

  const basicFeatures = [
    'Professional dryer vent cleaning',
    'Inspection of dryer vent system',
    'Removal of lint and debris',
    'Safety check of vent connections',
    'Basic maintenance report'
  ]

  const fullFeatures = [
    'Everything in Basic Package',
    'HVAC Filters cleaned/replaced',
    'Service CO & smoke detectors',
    'Test gas fireplace',
    'Inspect dishwasher components',
    'Run dishwasher cleaning cycle',
    'Check refrigerator and freezer temps',
    'Inspect and clean dryer vent and hose',
    'Clean gutters',
    'Complete home safety inspection',
    'Detailed maintenance report',
    'Priority scheduling for future services'
  ]

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-24 sm:pt-28 pb-16">
        {/* Hero Section */}
        <section className="py-16" style={{ backgroundColor: bgCream }}>
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Content */}
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6" style={{ backgroundColor: accentColor, color: 'white' }}>
                  <Timer className="h-4 w-4" />
                  Limited Time October Offer - Ends Soon!
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ color: heading }}>
                  $100 Gift Card for Dryer Vent Cleaning
                </h1>
                
                <p className="text-xl mb-8" style={{ color: sub }}>
                  Try our professional dryer vent cleaning service risk-free. Use your gift card now or save it for later.
                  Only pay $99 after service - satisfaction guaranteed.
                </p>


                <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4 justify-center lg:justify-start items-center mb-8">
                  <div className="text-center lg:text-left">
                    <div className="text-3xl font-bold" style={{ color: accentColor }}>$100 Gift Card</div>
                    <div className="text-sm" style={{ color: sub }}>Dryer Vent Cleaning Service</div>
                  </div>
                  <div className="text-gray-400 hidden sm:block">•</div>
                  <div className="text-center lg:text-left">
                    <div className="text-3xl font-bold" style={{ color: accentColor }}>Pay $99 After</div>
                    <div className="text-sm" style={{ color: sub }}>Only if satisfied</div>
                  </div>
                </div>

                {/* Primary CTA */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
                  <button 
                    onClick={() => document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Calendar className="h-5 w-5" />
                    Claim Your $100 Gift Card
                    <ArrowRight className="h-5 w-5" />
                  </button>
                  <a
                    href="https://api.leadconnectorhq.com/widget/booking/vGBU75grNpdx4TI2SvX2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                    style={{ borderColor: accentColor, color: accentColor }}
                  >
                    <Calendar className="h-5 w-5" />
                    Book Your Consultation
                  </a>
                </div>
              </div>

              {/* Right Side - Gift Card */}
              <div className="flex justify-center lg:justify-end">
                <div className="scale-90 lg:scale-100">
                  <GiftCard />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Simple Sign-Up Form */}
        <section id="signup-form" className="py-16">
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Steps */}
              <div>
                <h2 className="text-3xl font-semibold mb-6" style={{ color: heading }}>
                  How It Works
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      1
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: heading }}>
                        Get Free Gift Card
                      </h3>
                      <p className="text-sm" style={{ color: sub }}>
                        Receive your $100 gift card for new dryer vent cleaning services in exchange for your email address.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      2
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: heading }}>
                        Schedule a Time
                      </h3>
                      <p className="text-sm" style={{ color: sub }}>
                        Choose a convenient time below and our professional team will arrive at your home.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      3
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2" style={{ color: heading }}>
                        Enjoy Clean & Effortless Homeownership
                      </h3>
                      <p className="text-sm" style={{ color: sub }}>
                        Pay only $99 after service if you're satisfied. No upfront payment required.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Simple Form */}
              <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200">
                <h3 className="text-2xl font-semibold mb-6 text-center" style={{ color: heading }}>
                  Claim Your $100 Gift Card
                </h3>
                
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: heading }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: heading }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                  >
                    Get My $100 Gift Card
                  </button>

                  <p className="text-sm text-center" style={{ color: sub }}>
                    By submitting this form, you agree to receive your gift card and service information from Hudson My Home.
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters */}
        <section className="py-16">
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold mb-4" style={{ color: heading }}>
                Why Dryer Vent Cleaning Matters
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: sub }}>
                Clogged dryer vents are a leading cause of house fires and energy waste. 
                Our professional service ensures your home stays safe and efficient.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: heading }}>
                  Fire Prevention
                </h3>
                <p className="text-sm" style={{ color: sub }}>
                  Clogged dryer vents are responsible for over 15,000 house fires annually. 
                  Professional cleaning prevents dangerous lint buildup.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: heading }}>
                  Energy Efficiency
                </h3>
                <p className="text-sm" style={{ color: sub }}>
                  Clean vents reduce drying time by up to 30%, saving energy and extending 
                  the life of your dryer.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: heading }}>
                  Professional Service
                </h3>
                <p className="text-sm" style={{ color: sub }}>
                  Our certified technicians use professional equipment to thoroughly clean 
                  and inspect your entire dryer vent system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters - Pain Agitation */}
        <section className="py-16" style={{ backgroundColor: bgCream }}>
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold mb-4" style={{ color: heading }}>
                Don't Let This Happen to Your Home
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: sub }}>
                Dryer vent fires cause over $35 million in property damage annually. Most homeowners don't realize their dryer vent is a ticking time bomb.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {/* Pain Points */}
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold mb-6" style={{ color: heading }}>
                  The Hidden Dangers You're Living With:
                </h3>
                
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      <Shield className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: heading }}>Fire Risk</h4>
                      <p className="text-sm" style={{ color: sub }}>
                        Lint buildup creates a fire hazard that can destroy your home in minutes. 
                        Over 15,000 dryer fires occur each year.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      <Zap className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: heading }}>Higher Energy Bills</h4>
                      <p className="text-sm" style={{ color: sub }}>
                        Clogged vents force your dryer to work 3x harder, wasting energy and money every month.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accentColor }}>
                      <Wrench className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: heading }}>Home Damage</h4>
                      <p className="text-sm" style={{ color: sub }}>
                        Excess moisture from poor venting causes mold, mildew, and structural damage over time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Solution */}
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold mb-6" style={{ color: heading }}>
                  Professional Hudson Solution:
                </h3>
                
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-center mb-6">
                    <h4 className="text-xl font-semibold mb-2" style={{ color: heading }}>
                      $100 Gift Card - Use Now or Later
                    </h4>
                    <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>
                      Pay $99 After Service
                    </div>
                    <p className="text-sm" style={{ color: sub }}>
                      Professional Hudson technicians in Des Moines metro. Satisfaction guaranteed.
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {basicFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: accentColor }} />
                        <span className="text-sm" style={{ color: heading }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="text-center">
                    <button 
                      onClick={() => document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' })}
                      className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: accentColor }}
                    >
                      Claim Your $100 Gift Card
                    </button>
                    <p className="mt-3 text-sm" style={{ color: sub }}>
                      Use now or save for later • Professional Hudson technicians • Pay after service
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: accentColor, color: 'white' }}>
                <Shield className="h-4 w-4" />
                Licensed & Insured in Des Moines Metro
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16">
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold mb-4" style={{ color: heading }}>
                Why Choose Hudson for Your Dryer Vent Cleaning?
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>100%</div>
                <div className="text-sm" style={{ color: sub }}>Satisfaction Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>5★</div>
                <div className="text-sm" style={{ color: sub }}>Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2" style={{ color: accentColor }}>24/7</div>
                <div className="text-sm" style={{ color: sub }}>Emergency Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Area Map */}
        <section className="py-16" style={{ backgroundColor: bgCream }}>
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-semibold mb-4" style={{ color: heading }}>
                We Serve the Greater Des Moines Area
              </h2>
              <p className="text-lg" style={{ color: sub }}>
                Professional dryer vent cleaning services throughout Central Iowa
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Des Moines Map */}
              <div className="rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2988.5!2d-93.620866!3d41.586835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87ee99c0c6c0c6c0%3A0x8c0c6c0c6c0c6c0!2sDes%20Moines%2C%20IA!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Des Moines Service Area Map"
                ></iframe>
              </div>

              {/* Service Areas List */}
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold mb-6" style={{ color: heading }}>
                  Service Areas Include:
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    'Des Moines', 'West Des Moines', 'Urbandale', 'Johnston',
                    'Clive', 'Waukee', 'Ankeny', 'Altoona', 'Pleasant Hill',
                    'Windsor Heights', 'Grimes', 'Norwalk'
                  ].map((city, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4" style={{ color: accentColor }} />
                      <span className="text-sm" style={{ color: heading }}>{city}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <h4 className="font-semibold mb-2" style={{ color: heading }}>Not sure if we serve your area?</h4>
                  <p className="text-sm mb-3" style={{ color: sub }}>
                    Call us at (515) 216-4433 or email info@hudsonmyhome.com to check availability in your neighborhood.
                  </p>
                  <button 
                    onClick={() => setShowLeadForm(true)}
                    className="text-sm font-semibold"
                    style={{ color: accentColor }}
                  >
                    Check My Area →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16">
          <div className="mx-auto max-w-[1200px] px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heading }}>
                Ready to Keep Your Home Safe?
              </h2>
                      <p className="text-xl mb-8" style={{ color: sub }}>
                        Don't wait - dryer vent fires can happen to anyone. Claim your $100 gift card and schedule when convenient.
                      </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button 
                  onClick={() => setShowLeadForm(true)}
                  className="inline-flex items-center gap-2 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: accentColor }}
                >
                  <Calendar className="h-5 w-5" />
                  Claim $100 Gift Card
                  <ArrowRight className="h-5 w-5" />
                </button>
                <div className="text-sm" style={{ color: sub }}>
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    <span>30-day satisfaction guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

    </div>
  )
}

export default OctoberOfferPage
